import { Box, Paper, Typography } from "@mui/material";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { formatCurrency } from "../constants";

interface IncomeByBrandChartProps {
  data: LeadsManagementData;
}

export default function IncomeByBrandChart({ data }: IncomeByBrandChartProps) {
  const { aggregations, brandOptions, goToFilteredLeads } = data;

  // income_by_brand is computed server-side over the full filtered result
  // set (see the brand_leads_report/ response spec) and only carries
  // brand_name, not brand_uid — brandOptions (built from the brand
  // directory) is used to look up the uid a click needs to filter by.
  const brandUidByName = new Map(brandOptions.map((option) => [option.label, option.value]));

  const breakdown = [...aggregations.incomeByBrand.byBrand].sort((a, b) => b.amount - a.amount);
  const maxIncome = Math.max(1, ...breakdown.map((row) => row.amount));

  return (
    <Paper variant="outlined" data-tracker="969" sx={{ p: "20px 24px", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 500, color: "#1a1a1a" }}>
          Income by Brand
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#2E7D32" }}>
          {formatCurrency(aggregations.incomeByBrand.total)} total
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "#9e9e9e", mb: 2 }}>
        Income received from each brand partner for qualified leads.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
        {breakdown.length === 0 ? (
          <Typography sx={{ fontSize: 13.5, color: "#9e9e9e" }}>
            No brand income recorded yet.
          </Typography>
        ) : (
          breakdown.map((row) => {
            const brandUid = brandUidByName.get(row.brandName);
            return (
              <Box
                key={row.brandName}
                onClick={brandUid ? () => goToFilteredLeads({ brand: brandUid, qualified: "Yes" }) : undefined}
                title={brandUid ? "View these leads" : undefined}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: brandUid ? "pointer" : "default",
                }}
              >
                <Typography
                  sx={{ width: 150, fontSize: 12.5, color: "#616161", textAlign: "right", lineHeight: 1.2 }}
                >
                  {row.brandName}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: "#f3f3f5",
                    borderRadius: 1.5,
                    height: 22,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: 1.5,
                      bgcolor: "#2E7D32",
                      width: `${Math.round((row.amount / maxIncome) * 100)}%`,
                    }}
                  />
                </Box>
                <Typography sx={{ minWidth: 74, fontSize: 13, fontWeight: 700, color: "#424242", textAlign: "right" }}>
                  {formatCurrency(row.amount)}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
}
